/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { ROUTES } from 'constants';

import get from 'lodash/get';
import update from 'immutability-helper';

import {
  GET_DRAFT_INTERFACE_DETAILS,
  GET_DRAFT_INTERFACE_DETAILS_SUCCESS,
  GET_DRAFT_INTERFACE_DETAILS_FAILURE,
  GET_DRAFT_SCHEMA_RESOURCE_DETAILS,
  GET_DRAFT_SCHEMA_RESOURCE_DETAILS_SUCCESS,
  GET_DRAFT_SCHEMA_RESOURCE_DETAILS_FAILURE,
  GET_DRAFT_SCHEMA_CONTENT_DETAILS,
  GET_DRAFT_SCHEMA_CONTENT_DETAILS_SUCCESS,
  GET_DRAFT_SCHEMA_CONTENT_DETAILS_FAILURE,
  START_INTERFACE_DETAILS_EDIT_MODE,
  CANCEL_INTERFACE_DETAILS_EDIT_MODE,
  DELETE_DRAFT_INTERFACE,
  DELETE_DRAFT_INTERFACE_SUCCESS,
  DELETE_DRAFT_INTERFACE_FAILURE,
  SAVE_INTERFACE_DETAILS_EDITS,
  SAVE_INTERFACE_DETAILS_EDITS_SUCCESS,
  SAVE_INTERFACE_DETAILS_EDITS_FAILURE,
  GET_INTERFACE_DIFF,
  GET_INTERFACE_DIFF_SUCCESS,
  GET_INTERFACE_DIFF_FAILURE,
  GET_DEVICETYPE_DIFF,
  GET_DEVICETYPE_DIFF_SUCCESS,
  GET_DEVICETYPE_DIFF_FAILURE,
  CLEAR_INTERFACE_DETAILS,
  SHOW_CONFIRMATION_MODAL,
  HIDE_CONFIRMATION_MODAL,
  VALIDATE_INTERFACE,
  VALIDATE_INTERFACE_SUCCESS,
  VALIDATE_INTERFACE_FAILURE,
  VALIDATE_DEVICETYPE,
  VALIDATE_DEVICETYPE_SUCCESS,
  VALIDATE_DEVICETYPE_FAILURE,
  ACTIVATE_INTERFACE,
  ACTIVATE_INTERFACE_SUCCESS,
  ACTIVATE_INTERFACE_FAILURE,
  DEACTIVATE_INTERFACE,
  DEACTIVATE_INTERFACE_SUCCESS,
  DEACTIVATE_INTERFACE_FAILURE,
  CREATE_INTERFACE,
  CREATE_INTERFACE_SUCCESS,
  CREATE_INTERFACE_FAILURE,
} from 'interfacesActionTypes';

const getInitialConfirmationModalState = () => ({
  headingText: '',
  ariaLabel: '',
  iconDescription: '',
  primaryButtonText: '',
  secondaryButtonText: '',
  contentText: '',
  isLoading: false,
  visible: false,
  onCancel: undefined,
  onConfirm: undefined,
});

const getInitialDetailState = (
  initialDraftLiData,
  initialSchemaResource,
  initialSchemaContent
) => ({
  draftLi: {
    isLoading: false,
    error: undefined,
    data: initialDraftLiData,
  },
  draftSchemaResource: {
    isLoading: false,
    error: undefined,
    data: initialSchemaResource,
  },
  draftSchemaContent: {
    isLoading: false,
    error: undefined,
    data: initialSchemaContent,
  },
  diff: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },
  validation: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },
  dtDiff: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },
  dtValidation: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },
  inEditMode: false,
  inAddMode: false,
  isSaving: false,
  isActivating: false,
  confirmationModal: getInitialConfirmationModalState(),

  // allows reducer to invoke history.push in response to events (e.g. interface deletion)
  redirectTo: undefined,

  schemaTable: {
    instanceId: 'liSchemaTable',
    view: {
      table: {
        expandedIds: [],

        loadingState: {
          isLoading: false,
          rowCount: 10, // page size
        },
      },
    },
  },
});

export const reducer = (state = getInitialDetailState(), action) => {
  switch (action.type) {
    case ACTIVATE_INTERFACE: {
      return update(state, {
        isActivating: { $set: true },

        // invalidate diff and validation state, so they will be reloaded to reflect the new model
        // once the activation has completed (diff/validation loading is blocked while isActivating is true)
        diff: {
          $set: {
            isLoading: false,
            error: undefined,
            data: undefined,
          },
        },
        validation: {
          $set: {
            isLoading: false,
            error: undefined,
            data: undefined,
          },
        },

        dtDiff: {
          $set: {
            isLoading: false,
            error: undefined,
            data: undefined,
          },
        },
        dtValidation: {
          $set: {
            isLoading: false,
            error: undefined,
            data: undefined,
          },
        },
      });
    }

    case ACTIVATE_INTERFACE_SUCCESS:
    case ACTIVATE_INTERFACE_FAILURE: {
      return update(state, {
        isActivating: { $set: false },
      });
    }

    case DEACTIVATE_INTERFACE: {
      return update(state, {
        isDeactivating: { $set: true },

        // invalidate diff and validation state, so they will be reloaded to reflect the new model
        // once the activation has completed (diff/validation loading is blocked while isActivating is true)
        diff: {
          $set: {
            isLoading: false,
            error: undefined,
            data: undefined,
          },
        },
        validation: {
          $set: {
            isLoading: false,
            error: undefined,
            data: undefined,
          },
        },

        dtDiff: {
          $set: {
            isLoading: false,
            error: undefined,
            data: undefined,
          },
        },
        dtValidation: {
          $set: {
            isLoading: false,
            error: undefined,
            data: undefined,
          },
        },

        // and close the confirmation modal
        confirmationModal: {
          $set: getInitialConfirmationModalState(),
        },
      });
    }

    case DEACTIVATE_INTERFACE_SUCCESS:
    case DEACTIVATE_INTERFACE_FAILURE: {
      return update(state, {
        isDeactivating: { $set: false },
      });
    }

    case VALIDATE_INTERFACE: {
      return update(state, {
        validation: {
          isLoading: { $set: true },
          error: { $set: undefined },
          data: { $set: undefined },
        },
      });
    }
    case VALIDATE_INTERFACE_SUCCESS: {
      return update(state, {
        validation: {
          isLoading: { $set: false },
          error: { $set: undefined },
          data: { $set: action.payload },
        },
      });
    }
    case VALIDATE_INTERFACE_FAILURE: {
      return update(state, {
        validation: {
          isLoading: { $set: false },
          error: { $set: action.payload },
          data: { $set: undefined },
        },
      });
    }

    case SHOW_CONFIRMATION_MODAL: {
      return update(state, {
        confirmationModal: {
          $set: {
            isLoading: false,
            visible: true,
            onCancel: action.onCancel,
            onConfirm: action.onConfirm,
            headingText: action.headingText,
            ariaLabel: action.ariaLabel,
            iconDescription: action.iconDescription,
            primaryButtonText: action.primaryButtonText,
            secondaryButtonText: action.secondaryButtonText,
            contentText: action.contentText,
          },
        },
      });
    }
    case HIDE_CONFIRMATION_MODAL:
    case DELETE_DRAFT_INTERFACE_FAILURE: {
      return update(state, {
        confirmationModal: {
          $set: getInitialConfirmationModalState(),
        },
      });
    }

    case DELETE_DRAFT_INTERFACE: {
      return update(state, {
        confirmationModal: {
          isLoading: { $set: true },
        },
      });
    }

    case DELETE_DRAFT_INTERFACE_SUCCESS: {
      return update(state, {
        confirmationModal: {
          $set: getInitialConfirmationModalState(),
        },
        redirectTo: { $set: ROUTES.CONNECT_INTERFACES },
      });
    }

    case CLEAR_INTERFACE_DETAILS: {
      return getInitialDetailState(
        action.initialDraftLiData,
        action.initialSchemaResource,
        action.initialSchemaContent
      );
    }

    case GET_DRAFT_INTERFACE_DETAILS: {
      return update(state, {
        draftLi: {
          isLoading: { $set: true },
          error: { $set: undefined },
          data: { $set: undefined },
        },
      });
    }
    case GET_DRAFT_INTERFACE_DETAILS_SUCCESS: {
      return update(state, {
        draftLi: {
          isLoading: { $set: false },
          error: { $set: undefined },
          data: { $set: action.payload },
        },
      });
    }
    case GET_DRAFT_INTERFACE_DETAILS_FAILURE: {
      return update(state, {
        draftLi: {
          isLoading: { $set: false },
          error: { $set: action.payload },
          data: { $set: undefined },
        },
      });
    }

    case GET_DRAFT_SCHEMA_RESOURCE_DETAILS: {
      return update(state, {
        draftSchemaResource: {
          isLoading: { $set: true },
          error: { $set: undefined },
          data: { $set: undefined },
        },
      });
    }
    case GET_DRAFT_SCHEMA_RESOURCE_DETAILS_SUCCESS: {
      return update(state, {
        draftSchemaResource: {
          isLoading: { $set: false },
          error: { $set: undefined },
          data: { $set: action.payload }, //  payload includes resource and content
        },
      });
    }
    case GET_DRAFT_SCHEMA_RESOURCE_DETAILS_FAILURE: {
      return update(state, {
        draftSchemaResource: {
          isLoading: { $set: false },
          error: { $set: action.payload },
          data: { $set: undefined },
        },
      });
    }

    case GET_DRAFT_SCHEMA_CONTENT_DETAILS: {
      return update(state, {
        draftSchemaContent: {
          isLoading: { $set: true },
          error: { $set: undefined },
          data: { $set: undefined },
        },
      });
    }
    case GET_DRAFT_SCHEMA_CONTENT_DETAILS_SUCCESS: {
      return update(state, {
        draftSchemaContent: {
          isLoading: { $set: false },
          error: { $set: undefined },
          data: { $set: action.payload }, //  payload includes resource and content
        },
      });
    }
    case GET_DRAFT_SCHEMA_CONTENT_DETAILS_FAILURE: {
      return update(state, {
        draftSchemaContent: {
          isLoading: { $set: false },
          error: { $set: action.payload },
          data: { $set: undefined },
        },
      });
    }

    case START_INTERFACE_DETAILS_EDIT_MODE: {
      return update(state, {
        inEditMode: { $set: true },
      });
    }

    case CANCEL_INTERFACE_DETAILS_EDIT_MODE: {
      return update(state, {
        inEditMode: { $set: false },
      });
    }

    case SAVE_INTERFACE_DETAILS_EDITS:
    case CREATE_INTERFACE: {
      return update(state, {
        isSaving: { $set: true },
      });
    }

    case SAVE_INTERFACE_DETAILS_EDITS_SUCCESS: {
      const spec = {
        inEditMode: { $set: action.stayEditMode || false },
        isSaving: { $set: false },
        draftLi: {},
        draftSchemaResource: {},
        draftSchemaContent: {},

        // diff and validation results are now out of date,
        // so remove them - InterfaceDetailsContainer will detect this and reload them
        diff: {
          $set: {
            isLoading: false,
            error: undefined,
            data: undefined,
          },
        },
        validation: {
          $set: {
            isLoading: false,
            error: undefined,
            data: undefined,
          },
        },

        dtDiff: {
          $set: {
            isLoading: false,
            error: undefined,
            data: undefined,
          },
        },
        dtValidation: {
          $set: {
            isLoading: false,
            error: undefined,
            data: undefined,
          },
        },
      };

      // TODO: might be easier/safer just to clear data and let the container effects reload latest from API
      // (like we have done for ViewDeviceType)
      // and update data with that returned by the API
      if (action.updates.draftLi) {
        spec.draftLi.data = { $set: action.updates.draftLi };
      }

      if (action.updates.draftSchemaResource) {
        spec.draftSchemaResource.data = {
          $set: action.updates.draftSchemaResource,
        };
      }

      if (action.updates.draftSchemaContent) {
        spec.draftSchemaContent.data = {
          $set: action.updates.draftSchemaContent,
        };
      }

      return update(state, spec);
    }

    case SAVE_INTERFACE_DETAILS_EDITS_FAILURE: {
      // TODO: SAVE_INTERFACE_DETAILS_EDITS_FAILURE
      return state;
    }

    case GET_INTERFACE_DIFF: {
      return update(state, {
        diff: {
          isLoading: { $set: true },
          error: { $set: undefined },
          data: { $set: undefined },
        },
      });
    }
    case GET_INTERFACE_DIFF_SUCCESS: {
      return update(state, {
        diff: {
          isLoading: { $set: false },
          error: { $set: undefined },
          data: { $set: action.payload },
        },
      });
    }
    case GET_INTERFACE_DIFF_FAILURE: {
      return update(state, {
        diff: {
          isLoading: { $set: false },
          error: { $set: action.payload },
          data: { $set: undefined },
        },
      });
    }

    case GET_DEVICETYPE_DIFF: {
      return update(state, {
        dtDiff: {
          isLoading: { $set: true },
          error: { $set: undefined },
          data: { $set: undefined },
        },
      });
    }

    case GET_DEVICETYPE_DIFF_SUCCESS: {
      return update(state, {
        dtDiff: {
          isLoading: { $set: false },
          error: { $set: undefined },
          data: { $set: action.payload },
        },
      });
    }
    case GET_DEVICETYPE_DIFF_FAILURE: {
      return update(state, {
        dtDiff: {
          isLoading: { $set: false },
          error: { $set: action.payload },
          data: { $set: undefined },
        },
      });
    }

    case VALIDATE_DEVICETYPE: {
      return update(state, {
        dtValidation: {
          isLoading: { $set: true },
          error: { $set: undefined },
          data: { $set: undefined },
        },
      });
    }

    case VALIDATE_DEVICETYPE_SUCCESS: {
      return update(state, {
        dtValidation: {
          isLoading: { $set: false },
          error: { $set: undefined },
          data: { $set: action.payload },
        },
      });
    }
    case VALIDATE_DEVICETYPE_FAILURE: {
      return update(state, {
        dtValidation: {
          isLoading: { $set: false },
          error: { $set: action.payload },
          data: { $set: undefined },
        },
      });
    }

    // ==============
    // = CREATE
    // ==============

    case CREATE_INTERFACE_SUCCESS: {
      return update(state, {
        isSaving: { $set: false },
        draftLi: {
          data: { $set: action.payload },
        },
      });
    }

    case CREATE_INTERFACE_FAILURE: {
      return update(state, {
        isSaving: { $set: false },
      });
    }

    default:
      return state;
  }
};

export const selectors = {
  getDraftInterfaceDetails: state => get(state, 'interfaces.details.draftLi'),
  getDraftSchemaResourceDetails: state => get(state, 'interfaces.details.draftSchemaResource'),
  getDraftSchemaContentDetails: state => get(state, 'interfaces.details.draftSchemaContent'),
  getDraftDeviceTypes: state => get(state, 'interfaces.details.draftDeviceTypes'),
  getInEditMode: state => get(state, `interfaces.details.inEditMode`),
  isSaving: state => get(state, 'interfaces.details.isSaving'),
  isActivating: state => get(state, 'interfaces.details.isActivating'),
  isDeactivating: state => get(state, 'interfaces.details.isDeactivating'),

  getInAddMode: state => get(state, 'interfaces.details.inAddMode'),
  getInterfaceDifferences: state => get(state, 'interfaces.details.diff'),
  getDeviceTypeDifferences: state => get(state, 'interfaces.details.dtDiff'),
  getConfirmationModal: state => get(state, 'interfaces.details.confirmationModal'),
  getRedirectTo: state => get(state, 'interfaces.details.redirectTo'),
  getValidation: state => get(state, 'interfaces.details.validation'),
  getDeviceTypeValidation: state => get(state, 'interfaces.details.dtValidation'),
};
